/**
 * OfflineBanner Component
 *
 * Component that displays a banner when the app is offline.
 * Shows helpful message and provides retry functionality.
 */

import { useEffect, useState, useCallback } from 'react'
import { isOnline } from '@/lib/pwa'

interface OfflineBannerProps {
  onRetry?: () => void
}

export function OfflineBanner({ onRetry }: OfflineBannerProps) {
  const [online, setOnline] = useState(() => isOnline())

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)

    // Listen for online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleRetry = useCallback(() => {
    if (onRetry) {
      // Use custom retry handler
      onRetry()
    } else {
      // Default behavior: reload the page
      window.location.reload()
    }
  }, [onRetry])

  // Don't render if online
  if (online) {
    return null
  }

  return (
    <div
      role="banner"
      aria-live="polite"
      aria-label="Offline notification"
      className="fixed top-0 left-0 right-0 bg-amber-500 text-white px-4 py-3 z-50 shadow-md animate-slide-down"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Message */}
        <div className="flex items-center gap-3 flex-1">
          {/* Warning Icon */}
          <span className="text-xl flex-shrink-0" aria-hidden="true">
            ⚠️
          </span>

          {/* Text */}
          <div className="flex-1">
            <p className="font-medium text-sm">
              You're offline
            </p>
            <p className="text-xs text-amber-100 mt-0.5">
              Some features may be limited. Check your connection.
            </p>
          </div>
        </div>

        {/* Retry Button */}
        <button
          onClick={handleRetry}
          className="flex-shrink-0 bg-white text-amber-600 hover:bg-amber-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-amber-500"
          aria-label="Retry connection"
        >
          Retry
        </button>
      </div>
    </div>
  )
}
